<h1 class="page-title">{{ 'account.sections.contributions' | t }}</h1>

<div class="tabs" role="tablist">
  <button type="button" class="tab" role="tab" [class.active]="tab === 'activity'" (click)="tab = 'activity'">{{ 'account.contributions.tabActivity' | t }}</button>
  <button type="button" class="tab" role="tab" [class.active]="tab === 'deals'" (click)="tab = 'deals'">
    {{ 'account.contributions.tabDeals' | t }}
    @if (submissions.length) { <span class="count">{{submissions.length}}</span> }
  </button>
  <button type="button" class="tab" role="tab" [class.active]="tab === 'comments'" (click)="tab = 'comments'">{{ 'account.contributions.tabComments' | t }}</button>
</div>

<ng-template #emptyDeals>
  <div class="empty-state">
    <mat-icon class="empty-icon">campaign</mat-icon>
    <div>
      <h3>{{ 'account.contributions.emptyDeals' | t }}</h3>
      <p class="muted">{{ 'account.contributions.emptyDealsHint' | t }}</p>
      <a class="btn-pill btn-pill-accent" routerLink="/poster"><mat-icon>add</mat-icon>{{ 'account.contributions.postDeal' | t }}</a>
    </div>
  </div>
</ng-template>

@if (loading) {
  <p class="muted">{{ 'account.loading' | t }}</p>
} @else if (error) {
  <p class="error-text">{{error}}</p>
  <button type="button" class="btn-pill btn-pill-outline" (click)="load()">{{ 'account.retry' | t }}</button>
} @else {
  @switch (tab) {

    @case ('activity') {
      @if (activity.length === 0) {
        <ng-container [ngTemplateOutlet]="emptyDeals"></ng-container>
      } @else {
        @for (item of activity; track $index) {
          <div class="activity-row">
            <div class="activity-thumb">
              @if (item.pictureUrl) {
                <img [src]="item.pictureUrl" [appImgFallback]="item.storedPictureUrl" [alt]="item.productName" loading="lazy" />
              } @else {
                <mat-icon>{{ item.type === 'comment' ? 'forum' : 'local_offer' }}</mat-icon>
              }
            </div>
            <div class="activity-body">
              <span class="muted">
                @if (item.type === 'comment') {
                  {{ 'account.contributions.commentedOn' | t: { date: date(item.date) } }}
                } @else {
                  {{ 'account.contributions.sharedDeal' | t: { date: date(item.date) } }}
                  <span class="chip" [class]="'chip ' + item.status">{{ 'account.contributions.status.' + item.status | t }}</span>
                  @if (item.points) { <span class="points">+{{item.points}}</span> }
                }
              </span>
              <strong class="activity-text">{{item.text}}</strong>
              @if (item.type === 'comment' && item.productId) {
                <a class="muted product-link" [routerLink]="['/product', item.productId]">{{item.productName}}</a>
              } @else if (item.productId) {
                <a class="btn-text" [routerLink]="['/product', item.productId]">{{ 'account.contributions.viewDeal' | t }}</a>
              }
            </div>
          </div>
        }
        <p class="muted centered">{{ 'account.contributions.recentOnly' | t }}</p>
      }
    }

    @case ('deals') {
      @if (submissions.length === 0) {
        <ng-container [ngTemplateOutlet]="emptyDeals"></ng-container>
      } @else {
        @for (submission of submissions; track submission._id) {
          <article class="deal-row">
            <div class="deal-thumb">
              @if (submission.pictureUrl) {
                <img [src]="submission.pictureUrl" [alt]="submission.title" loading="lazy" />
              } @else {
                <mat-icon>local_offer</mat-icon>
              }
            </div>
            <div class="deal-body">
              <div class="deal-meta">
                <span [class]="'chip ' + submission.status">
                  <mat-icon>{{ submission.status === 'approved' ? 'check_circle' : submission.status === 'rejected' ? 'cancel' : 'schedule' }}</mat-icon>
                  {{ 'account.contributions.status.' + submission.status | t }}
                </span>
                @if (submission.partner ?? submission.affiliateStatus !== 'missing') {
                  <span class="chip"><mat-icon>handshake</mat-icon>{{ 'account.contributions.partner' | t }}</span>
                }
                <span>{{ date(submission.createdAt) }}</span>
              </div>
              @if (submission.productId) {
                <a class="deal-title" [routerLink]="['/product', submission.productId]">{{submission.title}}</a>
              } @else {
                <span class="deal-title">{{submission.title}}</span>
              }
              <div class="deal-meta">
                @if (submission.priceDiscount !== null && submission.priceDiscount !== undefined) {
                  <span class="price">{{submission.priceDiscount | number:'1.0-2'}}€</span>
                }
                @if (submission.price) {
                  <span class="old-price">{{submission.price | number:'1.0-2'}}€</span>
                }
                @if (submission.percentageDiscount) {
                  <span class="discount">-{{submission.percentageDiscount}}%</span>
                }
                @if (submission.promotionCode) {
                  <span class="chip"><mat-icon>content_cut</mat-icon>{{submission.promotionCode}}</span>
                }
                @if (submission.inStore) {
                  <span class="chip"><mat-icon>storefront</mat-icon>{{ 'inStore.badge' | t }}</span>
                }
                <span>{{submission.advertiserName || submission.merchantHost}}</span>
              </div>
              @if (submission.status === 'rejected' && submission.reviewNote) {
                <p class="review-note">{{ 'account.contributions.reason' | t: { note: submission.reviewNote } }}</p>
              }
              <div class="deal-actions">
                @if (submission.status === 'approved') {
                  <span class="points">{{ 'account.contributions.pointsEarned' | t: { points: submission.pointsAwarded } }}</span>
                } @else if (submission.status === 'pending') {
                  <span class="muted">{{ 'account.contributions.pendingHint' | t }}</span>
                  <button type="button" class="btn-text danger" (click)="cancel(submission)">{{ 'account.contributions.cancel' | t }}</button>
                } @else {
                  <span></span>
                }
                @if (submission.productId) {
                  <a class="btn-pill btn-pill-muted" [routerLink]="['/product', submission.productId]">{{ 'account.contributions.viewDeal' | t }}</a>
                }
              </div>
            </div>
          </article>
        }
      }
    }

    @case ('comments') {
      @if (comments.length === 0) {
        <div class="empty-state">
          <mat-icon class="empty-icon">forum</mat-icon>
          <div>
            <h3>{{ 'account.contributions.emptyComments' | t }}</h3>
            <p class="muted">{{ 'account.contributions.emptyCommentsHint' | t }}</p>
            <a class="btn-pill btn-pill-accent" routerLink="/">{{ 'account.saved.browse' | t }}</a>
          </div>
        </div>
      } @else {
        @for (item of comments; track $index) {
          <div class="activity-row">
            <div class="activity-thumb">
              @if (item.pictureUrl) {
                <img [src]="item.pictureUrl" [appImgFallback]="item.storedPictureUrl" [alt]="item.productName" loading="lazy" />
              } @else {
                <mat-icon>forum</mat-icon>
              }
            </div>
            <div class="activity-body">
              <span class="muted">{{ 'account.contributions.commentedOn' | t: { date: date(item.date) } }}</span>
              <strong class="activity-text">{{item.text}}</strong>
              @if (item.productId) {
                <a class="muted product-link" [routerLink]="['/product', item.productId]">{{item.productName}}</a>
              }
            </div>
          </div>
        }
      }
    }
  }
}
